<template>
  <section class="analytics-brand-table">
    <div class="sticky-header">
      <div class="list-header">
        <div class="ui-search-wrap">
          <Search @onenter="onSearch" :disabled="is_loading" :placeholder_text="search_placeholder" />
        </div>
        <div class="list-btn-box">
          <DropdownBrands
            v-if="start_date && end_date"
            :start_date="start_date"
            :end_date="end_date"
            :direction_type="'right'"
          />
          <Dropdown
            :items="sort_items"
            :direction_type="'right'"
            :icon="'filter'"
            :default_value="sort_index"
            :other_text="'Sort: '"
            @update-dropdown="onSortChange"
          />
          <Dropdown
            :items="per_page_items"
            :direction_type="'right'"
            :no_icon="true"
            :other_text="''"
            @update-dropdown="onPerPageChange"
          />
          <Pagenavi
            :disabled="last_page === 1"
            :page_current="current_page"
            :page_total="last_page"
            @goto-page="gotoPage"
          />
        </div>
      </div>

      <div class="short-link" v-if="data_statistic">
        <div class="report-box">
          <Loading v-if="is_loading" class="absolute" />
          <div class="report-item">
            <div class="datainfo-wrap">
              <div class="text">Total Products</div>
              <div class="value">
                {{ data_statistic.store_front_count != null ? data_statistic.store_front_count.toLocaleString('en-US') : '---' }}
              </div>
            </div>
          </div>
          <div class="report-item">
            <div class="datainfo-wrap">
              <div class="text">Product Views</div>
              <div class="value">
                {{ data_statistic.product_views_sum != null ? data_statistic.product_views_sum.toLocaleString('en-US') : '---' }}
              </div>
            </div>
          </div>
          <div class="report-item">
            <SValue
              :chart_type_text="'click'"
              :is_wrap="true"
              :sum="data_statistic.all_click"
              :value_text="'Clicks'"
              :show_sortlink="sortlink_data === 'all' && data_statistic?.can_toggle_sortlink"
              :sort_value="data_statistic.short_link_click_sum || 0"
              :store_value="data_statistic.click_sum || 0"
            />
          </div>
          <div class="report-item">
            <SValue
              :chart_type_text="'tran'"
              :is_wrap="true"
              :sum="data_statistic.all_sales"
              :value_text="'Sales'"
              :show_sortlink="sortlink_data === 'all' && data_statistic?.can_toggle_sortlink"
              :sort_value="data_statistic.short_link_sales_sum || 0"
              :store_value="data_statistic.sales_sum || 0"
            />
          </div>
          <div class="report-item">
            <SValue
              :chart_type_text="'sales'"
              :is_wrap="true"
              :sum="data_statistic.all_sales_value"
              :value_text="'Sales Value'"
              :show_sortlink="sortlink_data === 'all' && data_statistic?.can_toggle_sortlink"
              :sort_value="Math.ceil(data_statistic.short_link_sales_value_sum || 0)"
              :store_value="Math.ceil(data_statistic.sales_value_sum || 0)"
            />
          </div>
          <div class="report-item">
            <SValue
              :chart_type_text="'earnings'"
              :is_wrap="true"
              :sum="data_statistic.all_earnings"
              :value_text="'Earnings'"
              :show_sortlink="sortlink_data === 'all' && data_statistic?.can_toggle_sortlink"
              :sort_value="Math.ceil(data_statistic.short_link_earnings_sum || 0)"
              :store_value="Math.ceil(data_statistic.earnings_sum || 0)"
            />
          </div>
        </div>
      </div>
    </div>

    <Loading v-if="is_loading && !data_statistic" class="static" />

    <Scrollbar class="data-list-scroll" :show_bar="true">
      <div v-if="data.length !== 0" class="brand-table-view">
        <Loading v-if="is_loading" class="absolute" />
        <Table>
          <template #head>
            <tr>
              <th class="name-row">
                <span>Brands ({{ total }})</span>
              </th>
              <TableTh
                name="creator_count"
                title="Creator"
                :sort_value="sort"
                @sort="onColumnSort"
                content="Creator"
              />
              <TableTh
                name="total_products"
                title="Total Products"
                :sort_value="sort"
                @sort="onColumnSort"
                content="Total Products"
              />
              <TableTh
                name="views"
                title="Product Views"
                :sort_value="sort"
                @sort="onColumnSort"
                content="Product Views"
              />
              <TableTh
                name="click"
                title="Product Clicks"
                :sort_value="sort"
                @sort="onColumnSort"
                content="Product Clicks"
              />
              <TableTh name="sales" title="Sales" :sort_value="sort" @sort="onColumnSort" content="Sales" />
              <TableTh
                name="sales_value"
                title="Sales Value"
                :sort_value="sort"
                @sort="onColumnSort"
                content="Sales Value"
              />
              <TableTh
                name="earnings"
                title="Earnings"
                :sort_value="sort"
                @sort="onColumnSort"
                content="Earnings"
              />
            </tr>
          </template>

          <template #body>
            <tr v-for="(item, index) in data" :key="index" @click.stop="openDetail(item)">
              <td class="name-row">{{ item.brand_name }}</td>
              <td>
                <div class="creators-row">
                  <svg-icon name="users-2" />
                  {{ item.creator_count != null ? item.creator_count.toLocaleString('en-US') : '---' }}
                </div>
              </td>
              <td>{{ item.total_products != null ? item.total_products.toLocaleString('en-US') : '---' }}</td>
              <td>{{ item.views ? item.views.toLocaleString('en-US') : '---' }}</td>
              <td>{{ item.click ? item.click.toLocaleString('en-US') : '---' }}</td>
              <td>{{ item.sales ? item.sales.toLocaleString('en-US') : '---' }}</td>
              <td v-if="typeof item.sales_value === 'number'">
                ${{ item.sales_value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
              </td>
              <td v-else>---</td>
              <td v-if="typeof item.earnings === 'number'">
                ${{ item.earnings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
              </td>
              <td v-else>---</td>
            </tr>
          </template>
        </Table>
      </div>

      <div v-if="!is_loading && data.length === 0">
        <NoData :image="require('@/assets/images/pc.png')" title="No data available" text="" />
      </div>
    </Scrollbar>
  </section>
</template>

<script setup>
import { getCurrentInstance, onMounted, onBeforeUnmount } from 'vue';
import { useAnalyticsProductList, sort_items, per_page_items } from '@/composables/useAnalyticsProductList.js';
import Scrollbar from '@/components/ui/Scrollbar.vue';
import Search from '@/components/ui/Search.vue';
import Dropdown from '@/components/ui/Dropdown.vue';
import DropdownBrands from '@/components/ui/DropdownBrands.vue';
import Pagenavi from '@/components/ui/Pagenavi.vue';
import Loading from '@/components/ui/Loading.vue';
import NoData from '@/components/creator/NoData.vue';
import Table from '@/components/ui/Table.vue';
import TableTh from '@/components/ui/TableTh.vue';
import SValue from '@/components/summary/SummaryValue.vue';

const props = defineProps({
  search_placeholder: { type: String, default: 'Search by brand' },
  build_statistic_url: { type: Function, required: true },
  build_list_url: { type: Function, required: true },
  start_date: { type: String, default: '' },
  end_date: { type: String, default: '' },
});

const { proxy } = getCurrentInstance();
const EventBus = proxy.EventBus;

const {
  is_loading, data, data_statistic,
  current_page, last_page, total,
  sort, sort_index, sortlink_data,
  onSearch, onSortChange, onColumnSort, onBrandsChange: onBrandChange, onPerPageChange, gotoPage,
} = useAnalyticsProductList({
  buildStatisticUrl: props.build_statistic_url,
  buildListUrl: props.build_list_url,
});

const _onUpdateByBrands = (brands) => onBrandChange(brands);

onMounted(() => {
  EventBus.on('update-by-brands', _onUpdateByBrands);
});

onBeforeUnmount(() => {
  EventBus.off('update-by-brands', _onUpdateByBrands);
});

const openDetail = (item) => {
  EventBus.emit('open-brand-detail', item);
};
</script>

<style lang="scss">
.analytics-brand-table {
  padding-bottom: 24px;

  .sticky-header {
    position: sticky;
    top: 0;
    background: white;
    z-index: 20;
  }

  .list-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 0;

    .list-btn-box {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-left: auto;
    }
  }

  .ui-search-wrap {
    flex: 1;

    .ui-search-input .input {
      max-width: unset;
    }
  }

  .short-link {
    margin: 12px 0;

    .report-box {
      display: flex;
      position: relative;
      border: 1px solid rgba(0, 0, 0, 0.08);
      margin-bottom: 16px;

      .report-item {
        flex: 1;
        padding: 12px;
        border-right: 1px solid rgba(0, 0, 0, 0.08);

        &:last-child {
          border-right: none;
        }

        .datainfo-wrap {
            .text {
              font:var(--font-med-14);
              color: var(--text-secondary);
              margin-bottom: 4px;
            }

            .desc-text{
              font:var(--font-med-12);
            }
            .value {
              font: var(--font-med-20);
              color: var(--dark-blue);
            }
        }


        .the-type {
          width: 100%;
          border: none;
          padding: 0;
          background: transparent;
          margin-top: 8px;
        }
      }
    }
  }

  .data-list-scroll {
    overflow-y: visible;
    height: calc(98vh - 360px);
  }

  .brand-table-view {
    margin-top: 8px;
    cursor: pointer;
    position: relative;

    .ui-table thead {
      position: sticky;
      top: 0;
      background: white;
      z-index: 10;
    }

    .name-row { min-width: 180px; }

    .creators-row {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    svg {
      width: 12px;
      height: 12px;
      margin-right: 4px;
      vertical-align: middle;
    }
  }

  .tfoot tr td { vertical-align: top; }
}
</style>
